import React, { FunctionComponent, useEffect, useState, useRef } from 'react';
import { concat, equals } from 'ramda';
import { connect } from 'react-redux';
import { IProspect } from '../../../../common/models/prospect/IProspect';
import { IBindingCallback1 } from '../../../../common/models/callback';
import { IFilter } from '../../../../common/models/filter/IFilter';
import { IAppState } from '../../../../common/models/store/IAppState';
import {
  fetchProspectsRoutine,
  changeProspectStatusRoutine,
  fetchProspectCountersRoutine,
  updateProspectFilterRoutine
} from '../../routines';
import { getColumns } from './utils';
import Table from '../../../../components/Table';
import SearchInput from '../../../../components/SearchInput';
import { IAssignmentFilter } from '../../../../common/models/filter/IAssignmentFilter';
import AssignmentFilter from '../../../../containers/AssignmentFilter';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { ContactType } from '../../../../common/enums/ContactType';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { showContactProfileModalRoutine } from '../../../../routines/history';
import { IProspectStatus } from '../../../../common/models/prospect/IProspectStatus';
import { ProspectStatus } from '../../../../common/enums/ProspectStatus';
import { User } from '../../../../common/models/user/User';
import { AssignmentFilterType } from '../../../../common/enums/AssignmentFilterType';
import SidebarFilterRadio from '../../../../components/RadioFilterItem';
import { DashboardColor } from '../../../../common/enums/DashboardColor';
import ProspectsBulkActions from '../../../../containers/ProspectsBulkActions';
import Illustration from '../../../../components/Illustration';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { FormattedMessage } from 'react-intl';

interface IProspectsPaneProps {
  prospects: IProspect[];
  total: number;
  loading: boolean;
  counters: Record<ProspectStatus, number> | undefined;
  user: User;
  filter?: IFilter;
  assignmentFilter: AssignmentFilterType;
  setFilter: IBindingCallback1<{ filter: Partial<IFilter>, assignmentFilter?: AssignmentFilterType}>;
  fetchProspects: IBindingCallback1<IFilter>;
  fetchCounters: IBindingCallback1<IFilter>;
  showContactProfileModal: IBindingCallback1<ISelectedRole>;
  changeProspectStatus: IBindingCallback1<{ data: IProspectStatus; statusFilter?: ProspectStatus }>;
}

const ProspectsPane: FunctionComponent<IProspectsPaneProps> = ({
  prospects,
  total,
  loading,
  counters,
  user,
  filter,
  assignmentFilter,
  setFilter,
  fetchCounters,
  fetchProspects,
  changeProspectStatus,
  showContactProfileModal
}) => {
  const [selected, setSelected] = useState([] as IProspect[]);
  const refsArray = useRef<any>([]);
  const [totalCount, setTotalCount] = useState(total);

  useEffect(() => setTotalCount(total), [total]);

  const updateFilter = (newFilter: Partial<IFilter>, assigmentType?: AssignmentFilterType) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter) || (assigmentType && assignmentFilter !== assigmentType)) {
      setFilter({ filter: updatedFilter, assignmentFilter: assigmentType });
    }
  };

  const updateAssignmentFilter = ({ userIds, teamIds, assigmentType }: IAssignmentFilter) => {
    updateFilter({ ...filter, userIds, teamIds }, assigmentType);
  };

  const updateStatusFilter = (status: ProspectStatus) => updateFilter({
    statusFilter: status
  });

  const onCellClick = ({ accessor, original }: IRowAccessor<IProspect>) => {
    switch (accessor) {
      case 'prospectName':
        showContactProfileModal({
          contactId: original.contactId,
          role: original.id ? ContactType.Prospect : ContactType.Reference });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!filter) {
      updateFilter({
        from: 0,
        to: 25,
        input: '',
        sort: [{ id: 'optInDate', desc: true }],
        filter: [],
        userIds: [user.id],
        teamIds: undefined,
        statusFilter: ProspectStatus.Active
      });
    }
  }, []);

  useEffect(() => {
    if (filter) {
      fetchProspects(filter);
      fetchCounters(filter);
    }
  }, [filter]);
  if (!filter) return <></>;
  const loadData = (from: number, to: number) => {
    fetchProspects({ ...filter, from, to });
  };

  const updateProspectStatus = (data: IProspectStatus) => changeProspectStatus({
    data,
    statusFilter: filter.statusFilter as ProspectStatus
  });
  const noDataPlaceholder = equals(filter.userIds, [user.id]) && !filter.teamIds && !filter.input
    ? (
      <Illustration
        variant="fisher"
        height="22rem"
        header={<FormattedMessage id="prospectTable.noProspects" defaultMessage="Waiting for prospects..." />}
        description={(
          <FormattedMessage
            id="prospectTable.noProspectsHint"
            defaultMessage={`You don’t have any new Prospects right now.
            When references opt-in to learn more about career opportunities with you, they will show up here.`}
          />
        )}
      />
    ) : (
      <NoDataPlaceholder variant="default" />
    );

  return (
    <>
      <div
        className="d-flex flex-row align-items-start justify-content-start db-tab-radio"
      >
        <SidebarFilterRadio
          id={ProspectStatus.Active}
          name="prospectHeader.new"
          color={DashboardColor.Neutral}
          count={counters ? counters[ProspectStatus.Active] : 0}
          isSelected={filter.statusFilter === ProspectStatus.Active}
          onSelect={status => updateStatusFilter(status as ProspectStatus)}
        />
        <SidebarFilterRadio
          id={ProspectStatus.Archived}
          name="prospectHeader.archived"
          color={DashboardColor.BrightNeutral}
          count={counters ? counters[ProspectStatus.Archived] : 0}
          isSelected={filter.statusFilter === ProspectStatus.Archived}
          onSelect={status => updateStatusFilter(status as ProspectStatus)}
        />
      </div>
      <div className="db-tab-second-row db-tab-filter">
        <AssignmentFilter
          defaultCheckboxChecked={
            assignmentFilter === AssignmentFilterType.Custom
              ? concat(filter.userIds || [], filter.teamIds || [])
              : undefined
          }
          defaultRadioChecked={assignmentFilter}
          title="filterByAssignedUser"
          defaultTitle="Filter by assigned user"
          onChange={updateAssignmentFilter}
        />
      </div>
      <div className="db-tab-second-row db-tab-header d-flex align-items-end">
        <div className="db-tab-subheader d-flex justify-content-between">
          <SearchInput
            onClear={() => updateFilter({ input: '' })}
            value={filter.input}
            onChange={val => updateFilter({ input: val })}
          />
          <div className="d-flex">
            {selected.length > 0 && (
              <ProspectsBulkActions
                selected={selected}
                filter={filter.statusFilter as ProspectStatus}
                changeStatus={updateProspectStatus}
              />
            )}
          </div>
        </div>
      </div>
      <div className="db-tab-table-fill position-relative">
        <Table
          ref={ref => { refsArray.current[0] = ref; }}
          config={{
            columns: getColumns(filter.input, updateProspectStatus),
            data: { items: prospects, total: totalCount },
            onLoad: loadData,
            loading,
            onFilterUpdated: updateFilter,
            filteringEnabled: true,
            sortingEnabled: true,
            rowSelection: 'multiple',
            onSelectionChanged: selectedRows => setSelected(selectedRows),
            onCellClicked: onCellClick,
            defaultSorted: filter.sort,
            noDataPlaceholder
          }}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({
  dashboard: {
    prospects: {
      prospects,
      loading,
      total,
      counters,
      filter,
      assignmentFilter
    }
  },
  user: { user }
}: IAppState) => ({ prospects, loading, total, counters, user: user as User, filter, assignmentFilter });

const mapDispachToProps = {
  setFilter: updateProspectFilterRoutine,
  fetchProspects: fetchProspectsRoutine,
  showContactProfileModal: showContactProfileModalRoutine,
  changeProspectStatus: changeProspectStatusRoutine,
  fetchCounters: fetchProspectCountersRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(ProspectsPane);
